import html2canvas from "html2canvas";

const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
const idle = () => new Promise((r) => setTimeout(r, 0));
const EXPORT_CAPTURE_PAD_X = 180;
const EXPORT_CAPTURE_PAD_Y = 32;

function abortIfNeeded(signal) {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function getDefaultScale(explicitScale) {
  if (Number.isFinite(+explicitScale)) return +explicitScale;
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  return dpr >= 2 ? 1.5 : dpr > 1 ? 1.25 : 1;
}

function normalizeBg(bg) {
  const s = String(bg || "").trim();
  return s || "#ffffff";
}

async function yieldToUI(signal) {
  abortIfNeeded(signal);
  await nextFrame();
  abortIfNeeded(signal);
  await idle();
  abortIfNeeded(signal);
}

function copyCanvasBitmaps(fromRoot, toRoot) {
  const src = fromRoot.querySelectorAll("canvas");
  const dst = toRoot.querySelectorAll("canvas");
  const n = Math.min(src.length, dst.length);
  for (let i = 0; i < n; i++) {
    try {
      dst[i].width = src[i].width;
      dst[i].height = src[i].height;
      dst[i].getContext("2d")?.drawImage(src[i], 0, 0);
    } catch {}
  }
}

function prepareCloneForCapture(root) {
  if (!root) return;

  root.style.overflow = "visible";

  root
    .querySelectorAll(
      ".highcharts-container, .highcharts-root, .highcharts-data-labels, .highcharts-label, svg"
    )
    .forEach((el) => {
      try {
        el.style.overflow = "visible";
      } catch {}

      if (el.tagName?.toLowerCase() === "svg") {
        try {
          el.setAttribute("overflow", "visible");
        } catch {}
      }
    });
}

async function canvasFromNode(node, { scale, backgroundColor, windowWidth, signal }) {
  abortIfNeeded(signal);

  await yieldToUI(signal);

  const canvas = await html2canvas(node, {
    scale,
    backgroundColor,
    useCORS: true,
    allowTaint: true,
    logging: false,
    scrollX: 0,
    scrollY: 0,
    windowWidth,
    foreignObjectRendering: false,
    removeContainer: true,
  });

  abortIfNeeded(signal);
  return canvas;
}

function toImage(canvas, { imageFormat = "PNG", jpegQuality = 0.85 } = {}) {
  const fmt = String(imageFormat).toUpperCase();
  if (fmt === "PNG") return { data: canvas.toDataURL("image/png", 1), type: "PNG" };
  return { data: canvas.toDataURL("image/jpeg", jpegQuality), type: "JPEG" };
}

function trimCanvasWhitespace(canvas, { padding = 18, whiteThreshold = 250 } = {}) {
  const ctx = canvas?.getContext?.("2d");
  const width = canvas?.width || 0;
  const height = canvas?.height || 0;

  if (!ctx || !width || !height) return canvas;

  const { data } = ctx.getImageData(0, 0, width, height);

  let top = height;
  let left = width;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      const isVisible = a > 0;
      const isNearWhite =
        r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold;

      if (!isVisible || isNearWhite) continue;

      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }

  if (right < left || bottom < top) return canvas;

  const cropLeft = Math.max(0, left - padding);
  const cropTop = Math.max(0, top - padding);
  const cropRight = Math.min(width - 1, right + padding);
  const cropBottom = Math.min(height - 1, bottom + padding);

  const cropWidth = cropRight - cropLeft + 1;
  const cropHeight = cropBottom - cropTop + 1;

  if (cropWidth >= width && cropHeight >= height) return canvas;

  const trimmed = document.createElement("canvas");
  trimmed.width = cropWidth;
  trimmed.height = cropHeight;

  trimmed
    .getContext("2d")
    ?.drawImage(
      canvas,
      cropLeft,
      cropTop,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

  return trimmed;
}

function detectCardLayoutType(card, title = "") {
  const safeTitle = String(title || "").toLowerCase();

  if (card?.querySelector?.('[data-export-type="highlights"]')) return "highlights";

  if (card?.querySelector?.(".highcharts-pie-series")) return "circular";

  if (safeTitle.includes("nuvem")) return "wordcloud";

  return "default";
}

async function captureIsolatedNode(
  nodeToClone,
  { scale, backgroundColor, imageFormat, jpegQuality, signal }
) {
  abortIfNeeded(signal);

  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.background = backgroundColor || "#ffffff";
  host.style.pointerEvents = "none";

  const clone = nodeToClone.cloneNode(true);
  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    await yieldToUI(signal);

    const w = Math.ceil(host.getBoundingClientRect().width || 1200);
    const canvas = await canvasFromNode(host, { scale, backgroundColor, windowWidth: w, signal });

    return {
      ...toImage(canvas, { imageFormat, jpegQuality }),
      width: canvas.width,
      height: canvas.height,
    };
  } finally {
    host.remove();
  }
}

function buildCaptureNode({ headerEl, bodyEl, backgroundColor }) {
  const wrap = document.createElement("div");
  wrap.style.position = "fixed";
  wrap.style.left = "-10000px";
  wrap.style.top = "0";
  wrap.style.zIndex = "999999";
  wrap.style.pointerEvents = "none";
  wrap.style.background = backgroundColor || "#ffffff";
  wrap.style.overflow = "visible";
  wrap.style.boxSizing = "border-box";
  wrap.style.padding = `${EXPORT_CAPTURE_PAD_Y}px ${EXPORT_CAPTURE_PAD_X}px`;

  const w = Math.ceil(bodyEl.getBoundingClientRect().width || 1200);
  const captureWidth = w + EXPORT_CAPTURE_PAD_X * 2;
  wrap.style.width = `${captureWidth}px`;

  if (headerEl) wrap.appendChild(headerEl.cloneNode(true));
  const bodyClone = bodyEl.cloneNode(true);

  bodyClone.style.width = `${w}px`;
  bodyClone.style.margin = "0 auto";
  bodyClone.style.display = "flex";
  bodyClone.style.justifyContent = "center";
  bodyClone.style.alignItems = "center";
  bodyClone.style.overflow = "visible";

  wrap.appendChild(bodyClone);

  document.body.appendChild(wrap);
  copyCanvasBitmaps(bodyEl, bodyClone);
  prepareCloneForCapture(bodyClone);

  return { wrap, width: captureWidth };
}

function isSubjectsTableViewport(el) {
  return el?.getAttribute?.("data-export-viewport") === "subjects-table";
}

function findScrollableViewport(wrap) {
  return (
    wrap.querySelector('[data-export-viewport]') ||
    wrap.querySelector(".mantine-ScrollArea-viewport") ||
    null
  );
}

function findTableParts(wrap) {
  const table = wrap.querySelector("table");
  const tbody = table?.querySelector("tbody") || null;
  const rows = tbody ? Array.from(tbody.querySelectorAll("tr")) : [];
  return { table, tbody, rows };
}

async function runPool(tasks, concurrency = 1, onEachDone, signal) {
  const results = new Array(tasks.length);
  let idx = 0;

  concurrency = clamp(concurrency, 1, 2);

  async function worker() {
    while (true) {
      abortIfNeeded(signal);

      if (idx >= tasks.length) return;
      const my = idx++;
      abortIfNeeded(signal);

      results[my] = await tasks[my]();

      abortIfNeeded(signal);
      if (typeof onEachDone === "function") onEachDone(my + 1);
      await yieldToUI(signal);
    }
  }

  const workers = Array.from({ length: Math.max(1, concurrency) }, worker);
  await Promise.all(workers);
  return results;
}

export async function captureDashboardBlocks({
  selector = "#pdf-dashboard",
  includeFilters = false,
  scale = 1,
  backgroundColor = "#ffffff",
  captureConcurrency = 1,
  imageFormat = "JPEG",
  jpegQuality = 0.82,
  selectedTitles = null,

  cardSelector = ".dash-item, .dash-item--full",
  sectionHeaderSelector = ".dash-sec-head",
  onProgress,
  signal,
} = {}) {
  const bg = normalizeBg(backgroundColor);
  const usedScale = getDefaultScale(scale);

  const report = (progress, step) => {
    abortIfNeeded(signal);
    if (typeof onProgress === "function") onProgress({ progress, step });
  };

  abortIfNeeded(signal);

  const root = document.querySelector(selector);
  if (!root) throw new Error(`captureDashboardBlocks: não achou ${selector}`);

  const filtersEl =
    includeFilters && root.previousElementSibling ? root.previousElementSibling : null;

  document.body.classList.add("exporting-pdf");
  const prevScroll = window.scrollY || 0;

  try {
    report(2, "Preparando…");
    await yieldToUI(signal);
    await yieldToUI(signal);
    await yieldToUI(signal);
    abortIfNeeded(signal);

    window.scrollTo(0, 0);
    await yieldToUI(signal);
    await yieldToUI(signal);
    abortIfNeeded(signal);

    const pages = [];

    const kpiGrid1 = root.querySelector(".kpi-grid");
    if (kpiGrid1) {
      report(8, "Capturando KPIs principais…");
      const img = await captureIsolatedNode(kpiGrid1, {
        scale: usedScale,
        backgroundColor: bg,
        imageFormat,
        jpegQuality,
        signal,
      });
      pages.push({ kind: "kpis_main", title: "KPIs • Visão geral", ...img });
    }

    const kpiGrid2 = root.querySelectorAll(".kpi-grid")?.[1] || null;
    if (kpiGrid2) {
      report(14, "Capturando KPIs extras…");
      const img = await captureIsolatedNode(kpiGrid2, {
        scale: usedScale,
        backgroundColor: bg,
        imageFormat,
        jpegQuality,
        signal,
      });
      pages.push({ kind: "kpis_extra", title: "KPIs • Espaço", ...img });
    }

    if (filtersEl) {
      report(18, "Capturando filtros…");
      await yieldToUI(signal);

      const w = Math.ceil(filtersEl.getBoundingClientRect().width || 1200);
      const canvas = await canvasFromNode(filtersEl, {
        scale: usedScale,
        backgroundColor: bg,
        windowWidth: w,
        signal,
      });

      pages.push({
        kind: "filters",
        title: "Filtros",
        ...toImage(canvas, { imageFormat, jpegQuality }),
        width: canvas.width,
        height: canvas.height,
      });
    }

    report(22, "Organizando gráficos…");
    await yieldToUI(signal);

    const selectedTitleSet = Array.isArray(selectedTitles)
      ? new Set(selectedTitles.map((title) => String(title || "").trim()).filter(Boolean))
      : null;

    const cards = Array.from(root.querySelectorAll(cardSelector)).filter((card) => {
      if (!selectedTitleSet) return true;
      const title = String(card.getAttribute("data-export-title") || "").trim();
      return selectedTitleSet.has(title);
    });

    const tasks = cards.map((card) => async () => {
      abortIfNeeded(signal);

      const section = card.closest(".dash-section");
      const headerEl = section?.querySelector(sectionHeaderSelector) || null;

      const baseTitle =
        card.getAttribute("data-export-title") ||
        headerEl?.querySelector(".dash-sec-title")?.textContent?.trim() ||
        headerEl?.querySelector("h2")?.textContent?.trim() ||
        "Gráfico";

      const layoutType = detectCardLayoutType(card, baseTitle);

        const isHighlights = card.querySelector('[data-export-type="highlights"]');

        const chartContent = isHighlights ? card : (card.firstElementChild || card);

        const { wrap, width } = buildCaptureNode({
          headerEl: null,
          bodyEl: chartContent,
          backgroundColor: bg,
        });

        if (isHighlights) {
          const bodyClone = wrap.firstElementChild;

          if (bodyClone) {
            bodyClone.style.display = "block";
            bodyClone.style.width = "100%";
            bodyClone.style.margin = "0";
          }
        }

      try {
        const viewport = findScrollableViewport(wrap);

        if (!viewport) {
          const rawCanvas = await canvasFromNode(wrap, {
            scale: usedScale,
            backgroundColor: bg,
            windowWidth: width,
            signal,
          });
          const canvas = trimCanvasWhitespace(rawCanvas);

          return [
            {
              kind: "card",
              title: baseTitle,
              layoutType,
              ...toImage(canvas, { imageFormat, jpegQuality }),
              width: canvas.width,
              height: canvas.height,
            },
          ];
        }

        if (!isSubjectsTableViewport(viewport)) {
          const rawCanvas = await canvasFromNode(wrap, {
            scale: usedScale,
            backgroundColor: bg,
            windowWidth: width,
            signal,
          });
          const canvas = trimCanvasWhitespace(rawCanvas);

          return [
            {
              kind: "card",
              title: baseTitle,
              layoutType,
              ...toImage(canvas, { imageFormat, jpegQuality }),
              width: canvas.width,
              height: canvas.height,
            },
          ];
        }

        const { table, tbody, rows } = findTableParts(wrap);

        if (!table || !tbody || !rows.length) {
          const rawCanvas = await canvasFromNode(wrap, {
            scale: usedScale,
            backgroundColor: bg,
            windowWidth: width,
            signal,
          });
          const canvas = trimCanvasWhitespace(rawCanvas);

          return [
            {
              kind: "card",
              title: baseTitle,
              layoutType,
              ...toImage(canvas, { imageFormat, jpegQuality }),
              width: canvas.width,
              height: canvas.height,
            },
          ];
        }

        await yieldToUI(signal);

        const viewH = Math.ceil(viewport.clientHeight || 0);
        const firstRowH = Math.ceil(rows[0].getBoundingClientRect().height || 0);

        const rowsPerPage = 5;
        const totalParts = Math.max(1, Math.ceil(rows.length / rowsPerPage));

        const originalRows = rows.slice(); 

        const out = [];

        for (let part = 0; part < totalParts; part++) {
          abortIfNeeded(signal);

          const start = part * rowsPerPage;
          const end = Math.min(originalRows.length, start + rowsPerPage);
          const slice = originalRows.slice(start, end);

          while (tbody.firstChild) tbody.removeChild(tbody.firstChild);
          slice.forEach((tr) => tbody.appendChild(tr));

          await yieldToUI(signal);

          const rawCanvas = await canvasFromNode(wrap, {
            scale: usedScale,
            backgroundColor: bg,
            windowWidth: width,
            signal,
          });
          const canvas = trimCanvasWhitespace(rawCanvas);

          out.push({
            kind: "card",
            title: `${baseTitle} (${part + 1}/${totalParts})`,
            layoutType,
            ...toImage(canvas, { imageFormat, jpegQuality }),
            width: canvas.width,
            height: canvas.height,
          });
        }

        return out;
      } finally {
        wrap.remove();
      }
    });

    const totalCards = tasks.length;

    const captured = await runPool(
      tasks,
      captureConcurrency,
      (done) => {
        const p = 22 + Math.round((done / Math.max(1, totalCards)) * 64);
        report(p, `Capturando gráficos… (${done}/${totalCards})`);
      },
      signal
    );

    pages.push(...captured.flat());

    report(90, "Imagens prontas…");
    await yieldToUI(signal);
    return pages;
  } finally {
    document.body.classList.remove("exporting-pdf");
    window.scrollTo(0, prevScroll);
  }
}
